// src/components/auth/ResubscribeScreen.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Firebase and Stripe
import { functions as functionsInstance } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { PRICE_IDS } from '../../constants/stripeConstants';

// Contexts
import { useProfile } from '../../contexts/ProfileContext';
import { useLoading } from '../../contexts/LoadingContext';

// --- BEGIN Simple UI Components (similar to SignupScreen) ---
const ResubscribeLoadingSpinner = ({ message = "Loading..." }) => (
    <div className="flex flex-col items-center justify-center p-6">
        <svg className="animate-spin h-8 w-8 text-indigo-600 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-sm text-gray-600">{message}</p>
    </div>
);

const createFullscreenOverlay = () => {
    if (document.getElementById('resubscribe-checkout-overlay')) {
        return;
    }
    const overlay = document.createElement('div');
    overlay.id = 'resubscribe-checkout-overlay';
    Object.assign(overlay.style, {
        position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 0.95)', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        zIndex: '9999', transition: 'opacity 0.2s ease-in-out'
    });

    const logo = document.createElement('div');
    Object.assign(logo.style, {
        width: '56px', height: '56px', backgroundColor: '#4f46e5', borderRadius: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px'
    });
    logo.innerText = 'N²';

    const message = document.createElement('h2');
    Object.assign(message.style, {
        fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '8px'
    });
    message.innerText = 'Redirecting to Secure Checkout';

    const spinner = document.createElement('div');
    Object.assign(spinner.style, {
        width: '40px', height: '40px', border: '3px solid rgba(79, 70, 229, 0.2)',
        borderRadius: '50%', borderTopColor: '#4f46e5', marginTop: '24px',
        animation: 'spin 1s linear infinite'
    });
    if (!document.querySelector('style#spinAnimation')) {
        const styleSheet = document.createElement('style');
        styleSheet.id = 'spinAnimation';
        styleSheet.innerHTML = `@keyframes spin { to { transform: rotate(360deg); } }`;
        document.head.appendChild(styleSheet);
    }
    overlay.appendChild(logo);
    overlay.appendChild(message);
    overlay.appendChild(spinner);
    document.body.appendChild(overlay);
    overlay.getBoundingClientRect(); // Force reflow
    return overlay;
};

const performRedirect = (checkoutUrl) => {
    localStorage.setItem('pendingCheckoutUrl', checkoutUrl);
    createFullscreenOverlay();
    setTimeout(() => {
        window.location.href = '/checkout-redirect.html';
    }, 100);
};
// --- END Simple UI Components ---


// --- Initialize Cloud Function Reference ---
let initiateSubscriptionCheckout = null;
try {
    if (functionsInstance) {
        initiateSubscriptionCheckout = httpsCallable(functionsInstance, 'initiateSubscriptionCheckout');
        console.log("ResubscribeScreen: initiateSubscriptionCheckout function reference obtained.");
    } else {
        console.error("ResubscribeScreen: Firebase Functions instance is missing. Subscription initiation will fail.");
    }
} catch (error) {
    console.error("ResubscribeScreen: Error getting Firebase Functions reference:", error);
}
// --- Cloud Function Reference End ---

const ResubscribeScreen = () => {
    const navigate = useNavigate();
    const {
        authUser,
        isAuthLoading,
        userSubscriptionStatus,
        isSubscriptionLoading,
        userHadTrial, // To determine button text and potentially logic
    } = useProfile();
    const { showPersistentLoading, hideLoading, updateMessage } = useLoading();

    const [selectedPriceId, setSelectedPriceId] = useState(null);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [configError, setConfigError] = useState(null);

    const isMountedRef = useRef(true);
    const componentInstanceId = useRef(`ResubscribeScreen-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`).current;
    const redirectInProgressRef = useRef(false);


    // --- Environment Variable & Config Checks ---
    useEffect(() => {
        let errorMsg = null;
        if (!initiateSubscriptionCheckout) {
            errorMsg = "Config error (INIT_SUB).";
        }
        if (errorMsg) {
            setConfigError(errorMsg + " Subscription service unavailable. Please contact support.");
        }

        // Cleanup overlay if navigation happens before redirect completes
        return () => {
            const overlay = document.getElementById('resubscribe-checkout-overlay');
            if (overlay && overlay.parentNode && !redirectInProgressRef.current) {
                document.body.removeChild(overlay);
            }
        };
    }, []);

    // --- Mount/Unmount Ref Handling & Cleanup ---
    useEffect(() => {
        isMountedRef.current = true;
        console.log(`[${componentInstanceId} Mount Effect] Component mounted.`);
        
        // Clear any existing general checkout overlay from previous attempts if not already redirecting
        const existingGeneralOverlay = document.getElementById('immediate-checkout-overlay');
        if (existingGeneralOverlay && existingGeneralOverlay.parentNode) {
            existingGeneralOverlay.parentNode.removeChild(existingGeneralOverlay);
        }
        // Clear resubscribe-specific overlay too if it somehow exists and we are not mid-redirect
        const resubscribeOverlay = document.getElementById('resubscribe-checkout-overlay');
        if (resubscribeOverlay && resubscribeOverlay.parentNode && !redirectInProgressRef.current) {
             resubscribeOverlay.parentNode.removeChild(resubscribeOverlay);
        }


        return () => {
            isMountedRef.current = false;
            console.log(`[${componentInstanceId} Unmount Effect] Component unmounting.`);
            if (isProcessing && !redirectInProgressRef.current) {
                hideLoading();
            }
        };
    }, [componentInstanceId, isProcessing, hideLoading]);

    // --- Redirect if not applicable ---
    useEffect(() => {
        if (!isAuthLoading && !authUser) {
            console.log(`[${componentInstanceId}] User not authenticated. Redirecting to login.`);
            navigate('/login');
            return;
        }

        if (!isAuthLoading && authUser && !isSubscriptionLoading) {
            const activeStatuses = ['active', 'trialing'];
            if (activeStatuses.includes(userSubscriptionStatus)) {
                console.log(`[${componentInstanceId}] User has an active or trialing subscription. Redirecting to dashboard.`);
                navigate('/dashboard');
            }
            // Allow users with 'canceled' (but still active access period), 'past_due', 'unpaid'
            // to potentially choose a new plan here if they somehow land here, though AccountPage is better.
            // The main target is 'inactive', 'incomplete_expired'.
        }
    }, [
        isAuthLoading,
        authUser,
        isSubscriptionLoading,
        userSubscriptionStatus,
        navigate,
        componentInstanceId
    ]);
    
    // Check for pending redirect from previous attempt (e.g. page refresh during Stripe redirect)
    useEffect(() => {
        const pendingRedirect = localStorage.getItem('pendingCheckoutUrl');
        if (pendingRedirect) {
            console.log(`[${componentInstanceId}] Found pending checkout redirect, continuing...`);
            redirectInProgressRef.current = true;
            performRedirect(pendingRedirect); // performRedirect handles overlay
        }
    }, [componentInstanceId]);


    // --- Helper: Initiate Stripe Checkout ---
    const callInitiateCheckout = useCallback(async (priceId) => {
        if (!initiateSubscriptionCheckout) { throw new Error("Subscription service unavailable."); }
        if (!priceId) { throw new Error("No plan selected."); }
        if (!isMountedRef.current) return;

        createFullscreenOverlay(); // Show overlay immediately
        redirectInProgressRef.current = true; // Set flag
        showPersistentLoading("Redirecting to secure checkout...");
        
        console.log(`[${componentInstanceId} callInitiateCheckout] Calling for price ID: ${priceId}`);
        try {
            const result = await initiateSubscriptionCheckout({ priceId: priceId });
            const checkoutUrl = result?.data?.checkoutUrl;

            if (checkoutUrl) {
                if (isMountedRef.current) { // Check again before redirect
                    console.log(`[${componentInstanceId} callInitiateCheckout] Received checkout URL. Redirecting...`);
                    performRedirect(checkoutUrl); // This will use localStorage and redirect to checkout-redirect.html
                }
            } else {
                console.error(`[${componentInstanceId} callInitiateCheckout] Missing checkout URL:`, result);
                if (isMountedRef.current) {
                    hideLoading();
                    const overlay = document.getElementById('resubscribe-checkout-overlay');
                    if (overlay && overlay.parentNode) document.body.removeChild(overlay);
                    redirectInProgressRef.current = false;
                    throw new Error("Could not start the subscription process (missing URL).");
                }
            }
        } catch (err) {
            console.error(`[${componentInstanceId} callInitiateCheckout] Error:`, err);
            if (isMountedRef.current) {
                hideLoading();
                const overlay = document.getElementById('resubscribe-checkout-overlay');
                if (overlay && overlay.parentNode) document.body.removeChild(overlay);
                redirectInProgressRef.current = false;
                const message = err.message || "Failed to initiate checkout.";
                throw new Error(message);
            }
        }
    }, [componentInstanceId, showPersistentLoading, hideLoading]);

    // --- Main Subscribe Action ---
    const handleSubscribe = useCallback(async () => {
        console.log(`[${componentInstanceId} handleSubscribe] Initiated.`);
        if (isProcessing || !selectedPriceId || configError || redirectInProgressRef.current) {
            console.log(`[${componentInstanceId} handleSubscribe] Aborting: Conditions not met.`);
            if (configError) setError(configError);
            if (!selectedPriceId) setError("Please select a plan.");
            return;
        }
        if (!authUser) {
            setError("You must be logged in to subscribe.");
            return;
        }
        if (!isMountedRef.current) return;

        setIsProcessing(true);
        showPersistentLoading("Preparing your subscription...");
        setError(null);
        console.log(`[${componentInstanceId} handleSubscribe] State set to processing.`);

        try {
            updateMessage("Initiating secure checkout...");
            console.log(`[${componentInstanceId} handleSubscribe] Calling Stripe for price ${selectedPriceId}, user ${authUser.uid}...`);
            await callInitiateCheckout(selectedPriceId);
            // If callInitiateCheckout succeeds, it will redirect. Code here might not run if redirect is fast.
        } catch (error) {
            console.error(`[${componentInstanceId} handleSubscribe] Error:`, error);
            if (isMountedRef.current) {
                setError(error.message || "An unexpected error occurred. Please try again.");
                hideLoading();
                setIsProcessing(false);
            } else {
                hideLoading(); // Ensure loading is hidden if component unmounts during error
            }
            // Ensure overlay is removed on error if redirect didn't happen
            const overlay = document.getElementById('resubscribe-checkout-overlay');
            if (overlay && overlay.parentNode) document.body.removeChild(overlay);
            redirectInProgressRef.current = false;
        }
    }, [
        isProcessing, selectedPriceId, configError, authUser,
        callInitiateCheckout, componentInstanceId,
        showPersistentLoading, hideLoading, updateMessage
    ]);

    // --- Render Logic ---
    if (isAuthLoading || isSubscriptionLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <ResubscribeLoadingSpinner message="Loading account status..." />
            </div>
        );
    }

    const buttonText = userHadTrial ? "Subscribe" : "Subscribe";
    // Note: For a resubscribe screen, it's highly likely userHadTrial is true.
    // The `initiateSubscriptionCheckout` function will ultimately decide if a trial is granted based on `hasHadTrial` in Firestore.

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <div className={`bg-white p-8 rounded-lg shadow-md w-full max-w-md transition-opacity duration-200 ${isProcessing ? 'opacity-80' : 'opacity-100'}`}>
                <div className="flex flex-col items-center mb-6">
                    <div className="h-14 w-14 mb-3 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-lg flex items-center justify-center shadow-sm">
                        <span className="font-tomorrow font-semibold text-2xl text-white" style={{ fontFamily: 'Tomorrow, sans-serif', fontWeight: 600 }}>N²</span>
                    </div>
                    <h1 className="text-xl font-bold text-center text-gray-800 font-tomorrow" style={{ fontFamily: 'Tomorrow, sans-serif' }}>
                        Welcome Back!
                    </h1>
                    <p className="text-sm text-gray-600 mt-2 text-center">
                        Your previous subscription has ended. <br />Choose a plan to continue enjoying premium features.
                    </p>
                </div>

                {configError && (
                    <p className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                        {configError}
                    </p>
                )}

                <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 text-center">
                        Choose Your Plan
                    </h2>
                    <div className="space-y-3">
                        <button
                            type="button"
                            onClick={() => setSelectedPriceId(PRICE_IDS.monthly)}
                            className={`w-full flex flex-col items-center p-4 border rounded-md hover:shadow-md transition-all
                                ${selectedPriceId === PRICE_IDS.monthly ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500' : 'border-gray-300 hover:bg-gray-100'}
                            `}
                            disabled={isProcessing || !!configError}
                        >
                            <span className="text-md font-semibold text-indigo-700">Monthly Plan</span>
                            <span className="text-xs text-gray-600 mt-1">Flexible monthly billing.</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedPriceId(PRICE_IDS.yearly)}
                            className={`w-full flex flex-col items-center p-4 border rounded-md hover:shadow-md transition-all
                                ${selectedPriceId === PRICE_IDS.yearly ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500' : 'border-gray-300 hover:bg-gray-100'}
                            `}
                            disabled={isProcessing || !!configError}
                        >
                            <span className="text-md font-semibold text-indigo-700">Yearly Plan (Save ~17%)</span>
                            <span className="text-xs text-gray-600 mt-1">Best value, billed annually.</span>
                        </button>
                    </div>
                </div>

                {error && !configError && (
                    <p className="my-4 text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200 text-center" role="alert">
                        {error}
                    </p>
                )}

                <button
                    type="button"
                    onClick={handleSubscribe}
                    disabled={isProcessing || !selectedPriceId || !!configError}
                    className="w-full py-2.5 px-4 mt-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isProcessing ? 'Processing...' : buttonText}
                </button>

                <div className="mt-6 text-sm text-center">
                    <button
                        type="button"
                        onClick={() => navigate('/account')} // Changed from '/dashboard' to '/account'
                        className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={isProcessing}
                    >
                        Maybe Later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResubscribeScreen;